import { Component, inject, Input, OnInit } from '@angular/core';
import { AuthenticationService } from '@core/auth/authentication.service';
import { GrillaNavDirective, PegadoColumna } from '@shared/components/ui/grilla/grilla-nav.directive';
import { IconButton } from '@shared/components/ui/icon-button/icon-button';
import { CombosService } from '@shared/services/combos.service';
import { DialogService } from '@shared/services/dialog.service';
import { ErrorService } from '@shared/services/error.service';
import { Zona } from '@shared/services/interfaces/combos.interface';
import { CrearZonaModal } from '../../../ubicacion/zonas/crear-zona-modal/crear-zona-modal';

export type CampoEstablecimiento = 'nombre' | 'objetivo' | 'ubi_zona_id' | 'km' | 'direccion';

export interface FilaEstablecimiento {
  uid: number;
  nombre: string;
  objetivo: string;
  ubi_zona_id: string;
  km: string;
  direccion: string;
}

let proximoUid = 1;

export function filaEstablecimientoVacia(): FilaEstablecimiento {
  return { uid: proximoUid++, nombre: '', objetivo: '', ubi_zona_id: '', km: '', direccion: '' };
}

export function filaEstablecimientoConDatos(fila: FilaEstablecimiento): boolean {
  return [fila.nombre, fila.objetivo, fila.ubi_zona_id, fila.km, fila.direccion].some(valor => valor.trim() !== '');
}

/** Lo que falta para guardar la fila, o null si está completa. */
export function errorFilaEstablecimiento(fila: FilaEstablecimiento): string | null {
  const faltan = [
    !fila.nombre.trim() && 'nombre',
    !fila.objetivo && 'objetivo',
    !fila.ubi_zona_id && 'zona',
  ].filter(Boolean);
  if (faltan.length) return `Falta ${faltan.join(', ')}.`;
  if (fila.km.trim() && !(Number(fila.km) >= 0)) return 'El kilómetro debe ser un número positivo.';
  return null;
}

/** La fila como la recibe el backend (alta de cliente o POST de establecimientos). */
export function payloadFilaEstablecimiento(fila: FilaEstablecimiento) {
  return {
    nombre: fila.nombre.trim(),
    objetivo: fila.objetivo,
    ubi_zona_id: Number(fila.ubi_zona_id),
    km: fila.km.trim() ? Number(fila.km) : null,
    direccion: fila.direccion.trim() || null,
  };
}

/**
 * Establecimientos cargados como en Excel: una fila por establecimiento y siempre
 * una vacía abajo. El componente padre es dueño del arreglo; acá se escribe en él.
 */
@Component({
  selector: 'app-establecimientos-grilla',
  imports: [GrillaNavDirective, IconButton],
  templateUrl: './establecimientos-grilla.html',
  styleUrl: '../../../pive/planilla-lote/planilla-lote.css',
})
export class EstablecimientosGrilla implements OnInit {
  @Input({ required: true }) filas: FilaEstablecimiento[] = [];
  /** Muestra lo que le falta a cada fila (después de intentar guardar). */
  @Input() mostrarErrores = false;
  @Input() deshabilitada = false;

  zonas: Zona[] = [];
  readonly objetivos = [
    { value: 'E', label: 'Producción de embriones' },
    { value: 'P', label: 'Preñez' },
    { value: 'X', label: 'Producción de Embriones/Preñez' },
  ];

  /** Última fila tocada: la zona recién creada se le asigna a ella. */
  private filaActiva = 0;

  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private dialogService = inject(DialogService);
  private authService = inject(AuthenticationService);

  get puedeCrearZona(): boolean {
    return this.authService.canView('zonas') && this.authService.canCreate('zonas');
  }

  ngOnInit(): void {
    this.asegurarFilaVacia();
    this.cargarZonas();
  }

  onCambio(fila: FilaEstablecimiento, campo: CampoEstablecimiento, event: Event): void {
    fila[campo] = (event.target as HTMLInputElement | HTMLSelectElement).value;
    this.asegurarFilaVacia();
  }

  onFoco(indice: number): void {
    this.filaActiva = indice;
  }

  quitar(fila: FilaEstablecimiento): void {
    this.filas.splice(this.filas.indexOf(fila), 1);
    this.asegurarFilaVacia();
  }

  pegar({ columna, fila, valores }: PegadoColumna): void {
    valores.forEach((valor, offset) => {
      while (this.filas.length <= fila + offset) this.filas.push(filaEstablecimientoVacia());
      this.filas[fila + offset][columna as CampoEstablecimiento] = valor;
    });
    this.asegurarFilaVacia();
  }

  mensaje(fila: FilaEstablecimiento): string | null {
    return this.mostrarErrores && filaEstablecimientoConDatos(fila) ? errorFilaEstablecimiento(fila) : null;
  }

  tieneDatos(fila: FilaEstablecimiento): boolean {
    return filaEstablecimientoConDatos(fila);
  }

  crearZona(): void {
    this.dialogService
      .openForm(CrearZonaModal, { size: 'md' })
      .afterClosed()
      .subscribe((zona) => {
        if (!zona) return;
        this.cargarZonas(() => {
          const fila = this.filas[this.filaActiva] ?? this.filas[0];
          fila.ubi_zona_id = String(zona.id);
          this.asegurarFilaVacia();
        });
      });
  }

  private cargarZonas(despues?: () => void): void {
    this.combosService.getAllZonasCombo().subscribe({
      next: (response) => {
        this.zonas = response.data;
        despues?.();
      },
      error: (error) => this.errorService.handleError(error),
    });
  }

  /** Siempre queda una fila vacía abajo para seguir cargando. */
  private asegurarFilaVacia(): void {
    const ultima = this.filas[this.filas.length - 1];
    if (!ultima || filaEstablecimientoConDatos(ultima)) this.filas.push(filaEstablecimientoVacia());
  }
}
